import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, Crown, Plus, Pencil, Loader2, X, Archive, RotateCcw, Eye, EyeOff, AlertTriangle, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { errText } from "@/lib/errText";
import { daysUntil } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TierCard } from "@/components/creator/TierCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const MAX_ACTIVE = 5;

// Create / edit a fan tier. Remounted (via key) per-open so state is always fresh.
function TierDialog({ open, onOpenChange, tier, onDone }) {
  const isEdit = !!tier;
  const [name, setName] = useState(tier?.name || "");
  const [price, setPrice] = useState(tier ? String(tier.price_usd) : "");
  const [description, setDescription] = useState(tier?.description || "");
  const [perks, setPerks] = useState(tier?.perks?.length ? tier.perks : [""]);
  const [active, setActive] = useState(tier ? tier.active : true);
  const [busy, setBusy] = useState(false);

  const setPerk = (i, v) => setPerks((arr) => arr.map((x, idx) => (idx === i ? v : x)));
  const addPerk = () => setPerks((arr) => (arr.length < 8 ? [...arr, ""] : arr));
  const removePerk = (i) => setPerks((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

  const priceNum = Number(price);
  const nameOk = name.trim().length >= 2 && name.trim().length <= 40;
  const priceOk = Number.isFinite(priceNum) && priceNum >= 1 && priceNum <= 500;
  const ready = nameOk && priceOk;

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    const cleanPerks = perks.map((p) => p.trim()).filter(Boolean).slice(0, 8);
    const payload = {
      name: name.trim(),
      price_usd: priceNum,
      description: description.trim() || undefined,
      perks: cleanPerks,
    };
    try {
      if (isEdit) {
        await api.updateCreatorTier(tier.id, { ...payload, active });
        toast.success("Tier updated");
      } else {
        await api.createCreatorTier(payload);
        toast.success("Tier created");
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (e?.response?.status === 409) {
        toast.error(errText(e, `You can have at most ${MAX_ACTIVE} active tiers.`));
      } else if (e?.response?.status === 403 || code === "payouts_not_ready") {
        toast.error(errText(e, "Finish payout setup before creating tiers."));
      } else {
        toast.error(errText(e, "Couldn't save tier."));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-white/10 bg-bloq-s1 text-white" data-testid="tier-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Crown className="h-4 w-4 text-bloq-purple" /> {isEdit ? "Edit fan tier" : "New fan tier"}
          </DialogTitle>
          <DialogDescription className="text-bloq-muted">
            Monthly membership fans can subscribe to. You keep the majority of every payment — Stripe handles billing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
                placeholder="e.g. Inner Circle"
                className="h-11 rounded-xl border-white/12 bg-bloq-bg"
                data-testid="tier-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Price / mo</Label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-bloq-muted">$</span>
                <Input
                  type="number"
                  min={1}
                  max={500}
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="9.99"
                  className="h-11 rounded-xl border-white/12 bg-bloq-bg pl-6"
                  data-testid="tier-price-input"
                />
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-bloq-muted">Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={300}
              placeholder="What do members get at this tier?"
              className="min-h-20 rounded-xl border-white/12 bg-bloq-bg"
              data-testid="tier-desc-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-bloq-muted">Perks (up to 8)</Label>
            {perks.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={p}
                  onChange={(e) => setPerk(i, e.target.value)}
                  placeholder={`Perk ${i + 1}`}
                  className="h-10 rounded-xl border-white/12 bg-bloq-bg"
                  data-testid={`tier-perk-${i}`}
                />
                {perks.length > 1 && (
                  <button
                    onClick={() => removePerk(i)}
                    className="rounded-lg p-2 text-bloq-muted hover:text-destructive"
                    data-testid={`tier-perk-remove-${i}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {perks.length < 8 && (
              <button
                onClick={addPerk}
                className="flex items-center gap-1 text-xs font-semibold text-bloq-mint hover:opacity-80"
                data-testid="tier-add-perk"
              >
                <Plus className="h-3.5 w-3.5" /> Add perk
              </button>
            )}
          </div>
          {isEdit && (
            <div className="flex items-center justify-between rounded-xl border border-white/8 bg-bloq-s2 px-3 py-2.5">
              <div>
                <div className="text-sm font-semibold text-white">Active</div>
                <div className="text-xs text-bloq-muted">Inactive tiers stop taking new subscribers.</div>
              </div>
              <Switch checked={active} onCheckedChange={setActive} data-testid="tier-active-switch" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!ready || busy}
            className="w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90 disabled:opacity-60"
            data-testid="tier-submit-btn"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : isEdit ? "Save changes" : "Create tier"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TierRow({ tier, onEdit, onToggleActive, busy }) {
  const perks = (tier.perks || []).slice(0, 4);
  return (
    <div
      className={cn(
        "rounded-2xl border p-4",
        tier.active ? "border-white/8 bg-bloq-s2" : "border-white/8 bg-bloq-s2/50 opacity-70"
      )}
      data-testid={`tier-row-${tier.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-white" data-testid={`tier-name-${tier.id}`}>
              {tier.name}
            </span>
            {tier.active ? (
              <span className="rounded-full border border-bloq-mint/30 bg-bloq-mint/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-bloq-mint">
                Active
              </span>
            ) : (
              <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-bloq-muted">
                Retired
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-bloq-muted">
            <span className="font-semibold text-white" data-testid={`tier-price-${tier.id}`}>{usd(tier.price_usd)}</span> / month
            <span className="mx-1.5">·</span>
            <span data-testid={`tier-subs-${tier.id}`}>{tier.subscriber_count || 0} {tier.subscriber_count === 1 ? "subscriber" : "subscribers"}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            onClick={() => onEdit(tier)}
            className="rounded-lg p-2 text-bloq-muted hover:bg-white/5 hover:text-white"
            title="Edit tier"
            data-testid={`tier-edit-${tier.id}`}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={() => onToggleActive(tier)}
            disabled={busy}
            className={cn(
              "rounded-lg p-2 hover:bg-white/5 disabled:opacity-40",
              tier.active ? "text-bloq-muted hover:text-amber-400" : "text-bloq-muted hover:text-bloq-mint"
            )}
            title={tier.active ? "Retire tier" : "Reactivate tier"}
            data-testid={`tier-toggle-${tier.id}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : tier.active ? <Archive className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {tier.description && <p className="mt-2 text-sm text-white/80">{tier.description}</p>}
      {perks.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {perks.map((p, i) => (
            <span key={i} className="rounded-full border border-white/8 bg-bloq-bg px-2 py-0.5 text-[11px] text-bloq-muted">
              {p}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Lane B — creator self-management of monthly fan tiers.
export function FanTiersPanel({ userId, connectReady }) {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null); // null | { tier: null } (create) | { tier } (edit)
  const [togglingId, setTogglingId] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [showChurn, setShowChurn] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["creator-tiers", userId],
    queryFn: () => api.creatorTiers(userId),
    enabled: !!userId,
    retry: false,
  });
  const { data: subs } = useQuery({
    queryKey: ["creator-subscribers-summary"],
    queryFn: api.creatorSubscribers,
    retry: false,
  });

  const tiers = (data?.tiers || []).slice().sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0));
  const activeTiers = tiers.filter((t) => t.active);
  const activeCount = activeTiers.length;
  const lapsing = (subs?.subscribers || [])
    .filter((s) => { const d = daysUntil(s.expires_at); return d !== null && d <= 7; })
    .sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at));
  const atCap = activeCount >= MAX_ACTIVE;

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ["creator-tiers", userId] });
    qc.invalidateQueries({ queryKey: ["creator-subscribers-summary"] });
  };

  const toggleActive = async (tier) => {
    setTogglingId(tier.id);
    try {
      if (tier.active) {
        await api.deleteCreatorTier(tier.id);
        toast.success("Tier retired — current members keep access until it expires.");
      } else {
        await api.updateCreatorTier(tier.id, { active: true });
        toast.success("Tier reactivated");
      }
      refetch();
    } catch (e) {
      if (e?.response?.status === 409) toast.error(errText(e, `You can have at most ${MAX_ACTIVE} active tiers.`));
      else toast.error(errText(e, "Couldn't update tier."));
    } finally {
      setTogglingId(null);
    }
  };

  const newTierDisabled = !connectReady || atCap;
  const newTierHint = !connectReady
    ? "Finish payout setup above to start creating tiers."
    : atCap
    ? `You've reached the ${MAX_ACTIVE}-active-tier limit. Retire one to add another.`
    : null;

  return (
    <div className="rounded-3xl border border-white/8 bg-bloq-s1 p-6" data-testid="fan-tiers-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-white">
            <Users className="h-4 w-4 text-bloq-purple" /> Fan Tiers
          </div>
          <p className="mt-0.5 text-xs text-bloq-muted">
            Recurring monthly memberships. {subs?.count != null && (
              <span data-testid="fan-tiers-subs-summary">
                {subs.count} {subs.count === 1 ? "subscriber" : "subscribers"} · {usd(subs.net_usd)} net
              </span>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => setPreviewMode((v) => !v)}
            disabled={activeTiers.length === 0}
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-40",
              previewMode ? "border-bloq-mint/40 bg-bloq-mint/10 text-bloq-mint" : "border-white/12 text-bloq-muted hover:text-white"
            )}
            data-testid="tier-preview-toggle"
            title={activeTiers.length === 0 ? "No active tiers to preview" : "Preview how fans see your tiers"}
          >
            {previewMode ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {previewMode ? "Exit preview" : "Preview as fan"}
          </button>
          <Button
            onClick={() => setDialog({ tier: null })}
            disabled={newTierDisabled}
            className="shrink-0 rounded-full bg-bloq-purple font-bold text-white hover:bg-bloq-purple/90 disabled:opacity-50"
            data-testid="new-tier-btn"
          >
            <Plus className="mr-1 h-4 w-4" /> New tier
          </Button>
        </div>
      </div>

      {newTierHint && (
        <p className="mt-3 rounded-xl border border-amber-400/25 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-amber-300" data-testid="fan-tiers-hint">
          {newTierHint}
        </p>
      )}

      {lapsing.length > 0 && (
        <div className="mt-3" data-testid="creator-churn-panel">
          <button onClick={() => setShowChurn((v) => !v)} className="flex w-full items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-left" data-testid="churn-toggle">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-300" />
            <span className="flex-1 text-xs font-semibold text-amber-300">{lapsing.length} member{lapsing.length > 1 ? "s" : ""} lapsing this week</span>
            <ChevronDown className={cn("h-4 w-4 text-amber-300 transition-transform", showChurn && "rotate-180")} />
          </button>
          {showChurn && (
            <div className="mt-1.5 space-y-1" data-testid="churn-list">
              {lapsing.map((s, i) => {
                const d = daysUntil(s.expires_at);
                return (
                  <div key={s.user?.id || i} className="flex items-center justify-between gap-2 rounded-lg bg-bloq-s2 px-3 py-1.5 text-xs">
                    <span className="min-w-0 truncate text-white">{s.user?.display_name || `@${s.user?.username}`}<span className="ml-1.5 text-bloq-muted">{s.tier_name}</span></span>
                    <span className="shrink-0 font-semibold text-amber-300">{d < 0 ? "expired" : d === 0 ? "today" : `in ${d}d`}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-4">
        {isLoading ? (
          <div className="flex justify-center py-8 text-bloq-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : previewMode ? (
          <div data-testid="tier-preview-view">
            <p className="mb-3 rounded-xl border border-bloq-mint/20 bg-bloq-mint/[0.05] px-3 py-2 text-[11px] text-bloq-mint">
              This is exactly how fans see your active tiers before subscribing.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {activeTiers.map((t) => (
                <TierCard key={t.id} tier={t} preview />
              ))}
            </div>
          </div>
        ) : tiers.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 py-8 text-center" data-testid="fan-tiers-empty">
            <Crown className="mx-auto h-6 w-6 text-bloq-muted" />
            <p className="mt-2 text-sm text-bloq-muted">No fan tiers yet.</p>
            <p className="text-xs text-bloq-muted">Create your first tier to let fans subscribe monthly.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="fan-tiers-list">
            <div className="text-[11px] uppercase tracking-wider text-bloq-muted">{activeCount}/{MAX_ACTIVE} active</div>
            {tiers.map((t) => (
              <TierRow key={t.id} tier={t} onEdit={(tier) => setDialog({ tier })} onToggleActive={toggleActive} busy={togglingId === t.id} />
            ))}
          </div>
        )}
      </div>

      {dialog && (
        <TierDialog
          key={dialog.tier?.id || "new"}
          open
          onOpenChange={(o) => { if (!o) setDialog(null); }}
          tier={dialog.tier}
          onDone={refetch}
        />
      )}
    </div>
  );
}
